import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip,
  PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import { minMax } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';

const PIE_COLORS = ['#00d4ff', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#38bdf8', '#fb923c', '#4ade80', '#e879f9', '#8b949e'];

const CHART_TYPES = [
  { id: 'bar', label: 'Bar', icon: '▮' },
  { id: 'pie', label: 'Pie', icon: '◕' },
  { id: 'histogram', label: 'Histogram', icon: '▁▃▇' },
];

/**
 * The layer the chart opens on (V.31): the layer-tree's active layer when it is chartable, else
 * the first layer that is not demo data, else the first layer. `activeLayerKey` is a layer-tree
 * key ('imp:<id>' | 'res:<id>' | 'proj:<id>').
 */
export function defaultChartLayerId(layers, activeLayerKey) {
  const list = layers || [];
  if (activeLayerKey) {
    const [kind, ...rest] = String(activeLayerKey).split(':');
    const id = rest.join(':');
    const wanted = kind === 'proj' ? `usgs-${id}` : id;
    if (list.some(l => l.id === wanted)) return wanted;
  }
  const own = list.find(l => !String(l.id).startsWith('usgs-'));
  return (own || list[0])?.id || '';
}

/**
 * ChartPanel — interactive charts linked to the map (G35)
 *
 * Props:
 *   layers          - [{ id, name, geojson }] chartable layers
 *   activeFilter    - { layerId, attr, values?, range? } | null — current map filter
 *   onFilter(filter)- set/clear the map filter (null clears)
 *   mapSelection    - { layerId, properties } | null — feature clicked on map
 *   activeLayerKey  - layer-tree key of the active layer: the chart opens on it
 *   onClose()
 */
export default function ChartPanel({ layers = [], activeFilter, onFilter, mapSelection, activeLayerKey = null, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [layerId, setLayerId] = useState(() => defaultChartLayerId(layers, activeLayerKey));
  const [attr, setAttr] = useState('');
  const [chartType, setChartType] = useState('bar');

  const layer = layers.find(l => l.id === layerId);
  const features = useMemo(() => layer?.geojson?.features || [], [layer]);

  // Attribute options — keys present on features, split numeric vs categorical
  const attrInfo = useMemo(() => {
    const keys = {};
    features.slice(0, 500).forEach(f => {
      Object.entries(f.properties || {}).forEach(([k, v]) => {
        if (v == null || v === '') return;
        if (!keys[k]) keys[k] = { numeric: 0, total: 0 };
        keys[k].total++;
        if (typeof v === 'number' || (!isNaN(parseFloat(v)) && isFinite(v))) keys[k].numeric++;
      });
    });
    return Object.entries(keys).map(([k, info]) => ({
      key: k,
      isNumeric: info.numeric / info.total > 0.8,
    }));
  }, [features]);

  const currentAttr = attrInfo.find(a => a.key === attr) || attrInfo[0];
  const attrKey = currentAttr?.key;
  const isNumeric = currentAttr?.isNumeric;

  // Value of the map-selected feature for this attribute (for chart highlight)
  const selectedValue = (mapSelection && mapSelection.layerId === layerId && attrKey)
    ? mapSelection.properties?.[attrKey]
    : undefined;

  // Build chart data
  const chartData = useMemo(() => {
    if (!attrKey || features.length === 0) return [];

    if (chartType === 'histogram' && isNumeric) {
      const vals = features.map(f => parseFloat(f.properties?.[attrKey])).filter(v => !isNaN(v));
      if (vals.length === 0) return [];
      const { min, max } = minMax(vals);
      const nBins = Math.min(12, Math.max(4, Math.round(Math.sqrt(vals.length))));
      const width = (max - min) / nBins || 1;
      const bins = Array.from({ length: nBins }, (_, i) => ({
        name: `${(min + i * width).toFixed(1)}`,
        rangeMin: min + i * width,
        rangeMax: i === nBins - 1 ? max + 1e-9 : min + (i + 1) * width,
        count: 0,
      }));
      vals.forEach(v => {
        let idx = Math.floor((v - min) / width);
        if (idx >= nBins) idx = nBins - 1;
        bins[idx].count++;
      });
      return bins;
    }

    // Categorical counts (bar / pie)
    const counts = {};
    features.forEach(f => {
      let v = f.properties?.[attrKey];
      if (v == null || v === '') v = '(none)';
      if (isNumeric && chartType !== 'histogram') v = String(v);
      counts[v] = (counts[v] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name: String(name), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [features, attrKey, chartType, isNumeric]);

  // Is a chart segment the active filter?
  const isSegmentActive = (entry) => {
    if (!activeFilter || activeFilter.layerId !== layerId || activeFilter.attr !== attrKey) return false;
    if (activeFilter.range) return activeFilter.range[0] === entry.rangeMin;
    if (activeFilter.values) return activeFilter.values.includes(entry.name);
    return false;
  };

  // Is a segment the one containing the map-selected feature?
  const isSegmentSelected = (entry) => {
    if (selectedValue === undefined || selectedValue === null) return false;
    if (chartType === 'histogram' && entry.rangeMin !== undefined) {
      const v = parseFloat(selectedValue);
      return !isNaN(v) && v >= entry.rangeMin && v < entry.rangeMax;
    }
    return String(selectedValue) === entry.name;
  };

  const handleSegmentClick = (entry) => {
    if (!entry || !attrKey) return;
    if (isSegmentActive(entry)) {
      onFilter(null); // toggle off
      return;
    }
    if (chartType === 'histogram' && entry.rangeMin !== undefined) {
      onFilter({ layerId, attr: attrKey, range: [entry.rangeMin, entry.rangeMax] });
    } else {
      onFilter({ layerId, attr: attrKey, values: [entry.name] });
    }
  };

  const segColor = (entry, i) => {
    if (isSegmentActive(entry)) return C.amber;
    if (isSegmentSelected(entry)) return C.emerald;
    return chartType === 'pie' ? PIE_COLORS[i % PIE_COLORS.length] : C.accent;
  };

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };

  return (
    <div style={{ height: 260, background: C.bg2, borderTop: `1px solid ${C.border}`, display: 'flex', flexShrink: 0 }}>
      {/* Controls */}
      <div style={{ width: 200, borderRight: `1px solid ${C.border}`, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>📊 Charts</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text2, cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>

        <div>
          <label style={{ fontSize: 10, color: C.text2 }}>Layer</label>
          <select value={layerId} onChange={e => { setLayerId(e.target.value); setAttr(''); onFilter(null); }} style={selStyle}>
            {layers.length === 0 && <option value="">No layers</option>}
            {layers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: 10, color: C.text2 }}>Attribute</label>
          <select value={attrKey || ''} onChange={e => { setAttr(e.target.value); onFilter(null); }} style={selStyle}>
            {attrInfo.map(a => <option key={a.key} value={a.key}>{a.key}{a.isNumeric ? ' (#)' : ''}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: 10, color: C.text2 }}>Chart Type</label>
          <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
            {CHART_TYPES.map(t => (
              <button key={t.id} onClick={() => { setChartType(t.id); onFilter(null); }}
                disabled={t.id === 'histogram' && !isNumeric}
                title={t.id === 'histogram' && !isNumeric ? 'Requires numeric attribute' : t.label}
                style={{ flex: 1, fontSize: 10, padding: '5px 4px', borderRadius: 6, cursor: t.id === 'histogram' && !isNumeric ? 'not-allowed' : 'pointer', background: chartType === t.id ? C.bg4 : 'transparent', color: chartType === t.id ? C.accent : C.text2, border: `1px solid ${chartType === t.id ? C.accentBorder : C.border}`, opacity: t.id === 'histogram' && !isNumeric ? 0.4 : 1 }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ fontSize: 10, color: C.text2, lineHeight: 1.5 }}>
          {features.length} features · click a {chartType === 'pie' ? 'slice' : 'bar'} to filter the map
        </div>

        {activeFilter && activeFilter.layerId === layerId && (
          <button onClick={() => onFilter(null)}
            style={{ fontSize: 10, padding: '5px 8px', borderRadius: 6, background: 'rgba(251,191,36,0.12)', color: C.amber, border: '1px solid rgba(251,191,36,0.25)', cursor: 'pointer' }}>
            ✕ Clear map filter
          </button>
        )}
        {selectedValue !== undefined && (
          <div style={{ fontSize: 10, color: C.emerald }}>
            Map selection: {attrKey} = {String(selectedValue)}
          </div>
        )}
      </div>

      {/* Chart area */}
      <div style={{ flex: 1, padding: '10px 14px', minWidth: 0 }}>
        {chartData.length === 0 ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text2, fontSize: 12 }}>
            {layers.length === 0 ? 'No chartable layers — import data or run an analysis first.' : 'No attribute data to chart.'}
          </div>
        ) : chartType === 'pie' ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} dataKey="count" nameKey="name" cx="50%" cy="50%"
                innerRadius={40} outerRadius={85} paddingAngle={2} stroke={C.bg2}
                onClick={(entry) => handleSegmentClick(entry?.payload || entry)}
                label={({ name, percent }) => `${name.length > 14 ? name.slice(0, 13) + '…' : name} ${(percent * 100).toFixed(0)}%`}
                style={{ cursor: 'pointer', fontSize: 10 }}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={segColor(entry, i)} opacity={activeFilter && activeFilter.layerId === layerId && !isSegmentActive(entry) ? 0.35 : 1} />
                ))}
              </Pie>
              <ChartTooltip contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }} itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: C.text2 }} interval={0} angle={chartData.length > 8 ? -35 : 0} textAnchor={chartData.length > 8 ? 'end' : 'middle'} height={chartData.length > 8 ? 46 : 22} stroke={C.border} />
              <YAxis tick={{ fontSize: 9, fill: C.text2 }} stroke={C.border} allowDecimals={false} />
              <ChartTooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }} itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }} />
              <Bar dataKey="count" onClick={(entry) => handleSegmentClick(entry?.payload || entry)} style={{ cursor: 'pointer' }} radius={[3, 3, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={segColor(entry, i)} opacity={activeFilter && activeFilter.layerId === layerId && !isSegmentActive(entry) ? 0.35 : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
